/////////////////////////////////////////////////////////////////////
// Products.js - Product list view for ConfigSelect
// Allows Sales/Customer users to browse and select products
// Admins can inline-edit product names and descriptions
/////////////////////////////////////////////////////////////////////
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { fetchProjects, updateActiveProject } from '../../actions/projectListActions';
import { updateActiveTabIndex, invalidateDrawing } from '../../actions/uiFlagsActions';
import { addLog } from '../../actions/notificationActions';
import authService from '../../services/authService';
import productsService from '../../services/productsService';
import './Products.css';

export class Products extends Component {
    constructor(props) {
        super(props);
        this.state = {
            searchTerm: '',
            loading: true,
            error: null,
            dbProducts: [],
            // Name editing state
            editingNameId: null,
            editingNameText: '',
            savingName: false,
            savedNameId: null,
            // Description editing state
            editingDescriptionId: null,
            editingDescriptionText: '',
            savingDescription: false,
            savedDescriptionId: null
        };
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleProductClick = this.handleProductClick.bind(this);
    }

    componentDidMount() {
        this.props.fetchProjects();
        this.loadDbProducts();
        setTimeout(function() {
            this.setState({ loading: false });
        }.bind(this), 1000);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.projectList !== this.props.projectList && this.props.projectList.projects) {
            if (this.state.loading) {
                this.setState({ loading: false });
            }
        }
    }

    async loadDbProducts() {
        try {
            var products = await productsService.getAll();
            this.setState({ dbProducts: products });
        } catch (err) {
            console.log('Could not load product details from database:', err);
        }
    }

    getDbProduct(projectLabel) {
        // Match APS project to our database product by name
        var dbProducts = this.state.dbProducts;
        for (var i = 0; i < dbProducts.length; i++) {
            if (dbProducts[i].productName === projectLabel || dbProducts[i].displayName === projectLabel) {
                return dbProducts[i];
            }
        }
        return null;
    }

    handleSearchChange(event) {
        this.setState({ searchTerm: event.target.value });
    }

    handleProductClick(product) {
        // Don't navigate if we're currently editing
        if (this.state.editingDescriptionId || this.state.editingNameId) return;

        this.props.updateActiveProject(product.id);
        this.props.invalidateDrawing();
        if (this.props.onProductSelected) {
            this.props.onProductSelected(product);
        } else {
            this.props.updateActiveTabIndex(1);
        }
        this.props.addLog('Selected product: ' + product.label);
    }

    // Name editing methods
    startEditingName(e, dbProduct, currentDisplayName) {
        e.stopPropagation();
        this.setState({
            editingNameId: dbProduct.id,
            editingNameText: dbProduct.displayName || currentDisplayName || ''
        });
    }

    cancelEditingName() {
        this.setState({
            editingNameId: null,
            editingNameText: ''
        });
    }

    async saveName(dbProduct) {
        var newName = this.state.editingNameText.trim();
        if (!newName) {
            this.cancelEditingName();
            return;
        }

        this.setState({ savingName: true });

        try {
            await productsService.update(dbProduct.id, {
                productName: dbProduct.productName,
                displayName: newName,
                description: dbProduct.description || '',
                bucketPersistent: dbProduct.bucketPersistent || '',
                bucketTemporary: dbProduct.bucketTemporary || '',
                bucketTransient: dbProduct.bucketTransient || '',
                inputFileZip: dbProduct.inputFileZip || '',
                rootFileName: dbProduct.rootFileName || '',
                drawingFilePath: dbProduct.drawingFilePath || '',
                viewableZipFile: dbProduct.viewableZipFile || '',
                thumbnailUrl: dbProduct.thumbnailUrl || '',
                referenceImageUrl: dbProduct.referenceImageUrl || '',
                quoteTemplateFile: dbProduct.quoteTemplateFile || '',
                inventorVersion: dbProduct.inventorVersion || 'Autodesk.Inventor+2026'
            });

            // Update local state
            var updatedProducts = this.state.dbProducts.map(function(p) {
                if (p.id === dbProduct.id) {
                    return Object.assign({}, p, { displayName: newName });
                }
                return p;
            });

            this.setState({
                dbProducts: updatedProducts,
                editingNameId: null,
                editingNameText: '',
                savingName: false,
                savedNameId: dbProduct.id
            });

            // Clear the "Saved" indicator after 2 seconds
            var self = this;
            setTimeout(function() {
                self.setState({ savedNameId: null });
            }, 2000);

        } catch (err) {
            alert('Failed to save name: ' + err.message);
            this.setState({ savingName: false });
        }
    }

    handleNameKeyDown(e, dbProduct) {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.saveName(dbProduct);
        } else if (e.key === 'Escape') {
            this.cancelEditingName();
        }
    }

    startEditingDescription(e, dbProduct) {
        e.stopPropagation();
        this.setState({
            editingDescriptionId: dbProduct.id,
            editingDescriptionText: dbProduct.description || ''
        });
    }

    cancelEditingDescription() {
        this.setState({
            editingDescriptionId: null,
            editingDescriptionText: ''
        });
    }

    async saveDescription(dbProduct) {
        var newDescription = this.state.editingDescriptionText.trim();
        this.setState({ savingDescription: true });

        try {
            await productsService.update(dbProduct.id, {
                productName: dbProduct.productName,
                displayName: dbProduct.displayName || '',
                description: newDescription,
                bucketPersistent: dbProduct.bucketPersistent || '',
                bucketTemporary: dbProduct.bucketTemporary || '',
                bucketTransient: dbProduct.bucketTransient || '',
                inputFileZip: dbProduct.inputFileZip || '',
                rootFileName: dbProduct.rootFileName || '',
                drawingFilePath: dbProduct.drawingFilePath || '',
                viewableZipFile: dbProduct.viewableZipFile || '',
                thumbnailUrl: dbProduct.thumbnailUrl || '',
                referenceImageUrl: dbProduct.referenceImageUrl || '',
                quoteTemplateFile: dbProduct.quoteTemplateFile || '',
                inventorVersion: dbProduct.inventorVersion || 'Autodesk.Inventor+2026'
            });

            // Update local state
            var updatedProducts = this.state.dbProducts.map(function(p) {
                if (p.id === dbProduct.id) {
                    return Object.assign({}, p, { description: newDescription });
                }
                return p;
            });

            this.setState({
                dbProducts: updatedProducts,
                editingDescriptionId: null,
                editingDescriptionText: '',
                savingDescription: false,
                savedDescriptionId: dbProduct.id
            });

            // Clear the "Saved" indicator after 2 seconds
            var self = this;
            setTimeout(function() {
                self.setState({ savedDescriptionId: null });
            }, 2000);

        } catch (err) {
            alert('Failed to save description: ' + err.message);
            this.setState({ savingDescription: false });
        }
    }

    handleDescriptionKeyDown(e, dbProduct) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.saveDescription(dbProduct);
        } else if (e.key === 'Escape') {
            this.cancelEditingDescription();
        }
    }

    getFilteredProducts() {
        var projectList = this.props.projectList;
        var searchTerm = this.state.searchTerm;

        if (!projectList || !projectList.projects) {
            return [];
        }

        var products = projectList.projects;
        var self = this;

        if (searchTerm.trim()) {
            var term = searchTerm.toLowerCase();
            products = products.filter(function(product) {
                var dbProduct = self.getDbProduct(product.label);
                var displayName = dbProduct ? (dbProduct.displayName || '') : '';
                var desc = dbProduct ? (dbProduct.description || '') : '';
                var company = dbProduct ? (dbProduct.companyName || '') : '';
                return product.label.toLowerCase().indexOf(term) !== -1 ||
                    (displayName && displayName.toLowerCase().indexOf(term) !== -1) ||
                    (desc && desc.toLowerCase().indexOf(term) !== -1) ||
                    (company && company.toLowerCase().indexOf(term) !== -1);
            });
        }

        return products;
    }

    renderProductRow(product) {
        var self = this;
        var thumbnailUrl = product.image || null;
        var isAdmin = authService.isAdmin();
        var dbProduct = this.getDbProduct(product.label);
        var displayName = (dbProduct && dbProduct.displayName) ? dbProduct.displayName : product.label;
        var description = (dbProduct && dbProduct.description) ? dbProduct.description : '';
        var companyName = (dbProduct && dbProduct.companyName) ? dbProduct.companyName : '';
        var defaultDescription = 'Configurable product \u2014 click to open in the 3D configurator, adjust parameters, generate BOM and drawings.';
        var isEditingDescription = dbProduct && this.state.editingDescriptionId === dbProduct.id;
        var justSavedDescription = dbProduct && this.state.savedDescriptionId === dbProduct.id;
        var isEditingName = dbProduct && this.state.editingNameId === dbProduct.id;
        var justSavedName = dbProduct && this.state.savedNameId === dbProduct.id;

        return (
            <div
                key={product.id}
                className="product-row"
                onClick={function() { self.handleProductClick(product); }}
            >
                <div className="product-row-thumbnail">
                    {thumbnailUrl ? (
                        <img
                            src={thumbnailUrl}
                            alt={product.label}
                            onError={function(e) {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div
                        className="no-thumbnail"
                        style={{ display: thumbnailUrl ? 'none' : 'flex' }}
                    >
                        📦
                    </div>
                </div>
                <div className="product-row-info">
                    {/* Name - inline editable for admins */}
                    {isEditingName ? (
                        <div className="product-row-name-edit" onClick={function(e) { e.stopPropagation(); }}>
                            <input
                                type="text"
                                className="product-name-input"
                                value={this.state.editingNameText}
                                onChange={function(e) { self.setState({ editingNameText: e.target.value }); }}
                                onKeyDown={function(e) { self.handleNameKeyDown(e, dbProduct); }}
                                onBlur={function() { self.saveName(dbProduct); }}
                                placeholder="Enter product name..."
                                autoFocus
                                disabled={this.state.savingName}
                            />
                            <span className="product-name-hint">Enter to save, Escape to cancel</span>
                        </div>
                    ) : (
                        <h3 className="product-row-name">
                            {displayName}
                            {isAdmin && dbProduct && (
                                <span
                                    className="product-name-edit-btn"
                                    onClick={function(e) { self.startEditingName(e, dbProduct, product.label); }}
                                    title="Edit name"
                                >
                                    ✏️
                                </span>
                            )}
                            {justSavedName && (
                                <span className="product-name-saved">✓ Saved</span>
                            )}
                        </h3>
                    )}

                    {/* Description - inline editable for admins */}
                    {isEditingDescription ? (
                        <div className="product-row-description-edit" onClick={function(e) { e.stopPropagation(); }}>
                            <textarea
                                className="product-description-textarea"
                                value={this.state.editingDescriptionText}
                                onChange={function(e) { self.setState({ editingDescriptionText: e.target.value }); }}
                                onKeyDown={function(e) { self.handleDescriptionKeyDown(e, dbProduct); }}
                                onBlur={function() { self.saveDescription(dbProduct); }}
                                placeholder="Enter a product description..."
                                rows="2"
                                autoFocus
                                disabled={this.state.savingDescription}
                            />
                            <span className="product-description-hint">Enter to save, Escape to cancel</span>
                        </div>
                    ) : (
                        <p className="product-row-description">
                            {description || defaultDescription}
                            {isAdmin && dbProduct && (
                                <span
                                    className="product-description-edit-btn"
                                    onClick={function(e) { self.startEditingDescription(e, dbProduct); }}
                                    title="Edit description"
                                >
                                    ✏️
                                </span>
                            )}
                            {justSavedDescription && (
                                <span className="product-description-saved">✓ Saved</span>
                            )}
                        </p>
                    )}

                    <div className="product-row-details">
                        {companyName && <span className="product-row-tag company">{companyName}</span>}
                        {product.isAssembly && <span className="product-row-tag">Assembly</span>}
                        {product.hasDrawing && <span className="product-row-tag">Drawing Available</span>}
                    </div>
                </div>
                <div className="product-row-action">
                    <button className="product-row-button">
                        Configure →
                    </button>
                </div>
            </div>
        );
    }

    render() {
        var loading = this.state.loading;
        var searchTerm = this.state.searchTerm;

        if (loading) {
            return (
                <div className="products-container">
                    <div className="products-loading">
                        Loading products...
                    </div>
                </div>
            );
        }

        var products = this.getFilteredProducts();

        return (
            <div className="products-container">
                <div className="products-header">
                    <div className="products-header-left">
                        <h2>Products</h2>
                        <span className="products-count">{products.length} product{products.length !== 1 ? 's' : ''} available</span>
                    </div>
                    <div className="products-search">
                        <input
                            type="text"
                            placeholder="Filter products by name..."
                            value={searchTerm}
                            onChange={this.handleSearchChange}
                        />
                    </div>
                </div>

                {products.length === 0 ? (
                    <div className="products-empty">
                        <div className="products-empty-icon">📦</div>
                        <h3>No Products Available</h3>
                        <p>
                            {searchTerm
                                ? 'No products match your search. Try a different term.'
                                : 'No products have been assigned to your account yet. Please contact your administrator.'
                            }
                        </p>
                    </div>
                ) : (
                    <div className="products-list">
                        {products.map(function(product) { return this.renderProductRow(product); }.bind(this))}
                    </div>
                )}
            </div>
        );
    }
}

/* istanbul ignore next */
export default connect(
    function(store) {
        return {
            projectList: store.projectList
        };
    },
    { fetchProjects, updateActiveProject, updateActiveTabIndex, addLog, invalidateDrawing }
)(Products);